import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import CaseList from "../CaseList";
import CaseNavList from "../CaseNavList";
import CaseForm from "../CaseForm";
import { TableListLoader } from "../../../../components/Loader/TableListLoader";
import { FormLoader } from "../../../../components/Loader/FormLoader";
import {
  getPolicyDetailById,
  getPolicyListItems,
} from "../../../../redux/Case/policySlice";
import { POLICY_CASE_TYPE_MAP } from "./PolicyHomePage";
import { normalizeCaseType, isCurrentStatus, getItemStatus } from "../../../../shared/caseActiveStatus";
import { useToastMessage } from "../../../../components/ToastMessage/ToastMessage";
import { Toaster } from "@fluentui/react-components";

interface PolicyDetailPageProps {
  tab?: string;
}

const PolicyDetailPage: React.FC<PolicyDetailPageProps> = ({ tab }) => {
  const { clientId, case_type, action, caseId } = useParams();
  const navigate = useNavigate();
  const { showToast, toasterId } = useToastMessage();
  const dispatch: AppDispatch = useDispatch();
  const [currentRecord, setCurrentRecord] = useState(1);
  const [showNonCurrent, setShowNonCurrent] = useState(false);
  const hasAutoSwitched = useRef(false);
  const [nonCurrentExists, setNonCurrentExists] = useState(false);

  const { activeItem, listItems, loading, activeItemloading } = useSelector(
    (state: RootState) => state.policies
  );

  const dbCaseType = case_type ? POLICY_CASE_TYPE_MAP[case_type] : undefined;
  // caseContextList.policies is keyed by display name (e.g. "Life Assurance"),
  // not the URL slug, so CaseForm's context lookup needs the mapped name.
  const caseType = dbCaseType || "";

  useEffect(() => {
    setShowNonCurrent(false);
    setNonCurrentExists(false);
    hasAutoSwitched.current = false;
  }, [case_type]);

  useEffect(() => {
    if (action && action?.length > 5) {
      navigate(
        `/clients/${clientId}/policies/${case_type}/${action}/${tab || "policy"}/view`
      );
    }
    if (!action && caseId) {
      navigate(
        `/clients/${clientId}/policies/${case_type}/${caseId}/${tab || "policy"}/view`
      );
    }
  }, [action, caseId]);

  useEffect(() => {
    if (clientId) {
      dispatch(getPolicyListItems({ customerId: clientId, caseType: dbCaseType }))
        .then((result: any) => {
          if (!showNonCurrent && !hasAutoSwitched.current) {
            const items = Array.isArray(result?.payload) ? result.payload : [];
            const byType = dbCaseType
              ? items.filter((x: any) => normalizeCaseType(x.case_type) === normalizeCaseType(dbCaseType))
              : items;
            const hasCurrentRecords = byType.some((x: any) => isCurrentStatus("policy", getItemStatus(x)));
            if (!hasCurrentRecords && byType.length > 0) {
              hasAutoSwitched.current = true;
              setNonCurrentExists(true);
              setShowNonCurrent(true);
            }
          }
        });
    }
  }, [clientId, dbCaseType, dispatch]);

  useEffect(() => {
    if (caseId && clientId) {
      dispatch(getPolicyDetailById({ id: caseId, customerId: clientId }));
    }
  }, [caseId, clientId, dispatch]);

  const caseTypeListItems = useMemo(() => {
    const byType = dbCaseType
      ? listItems.filter((item: any) => normalizeCaseType(item.case_type) === normalizeCaseType(dbCaseType))
      : listItems;
    return !showNonCurrent
      ? byType.filter((item: any) => isCurrentStatus("policy", getItemStatus(item)))
      : byType;
  }, [listItems, dbCaseType, showNonCurrent]);

  const isEmptyCurrentView =
    nonCurrentExists && !showNonCurrent && caseTypeListItems.length === 0;
  // isEmptyCurrentView is meant to hide the form for a stale/no-longer-visible
  // selected record -- it must never block Add New. "add"/"edit" don't depend
  // on any existing record for the active toggle, so they're exempted here.
  // Without this, e.g. creating a case then editing its status to a
  // non-current one (auto-switching the toggle to Non-current), then toggling
  // back to Current Cases and clicking Add New left isEmptyCurrentView true
  // for the "add" route too, and the Add New form silently failed to render.
  const canRenderForm =
    !isEmptyCurrentView || ["add", "edit"].includes(action || "");

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      {action && (
        <CaseNavList
          menuItem={undefined}
          section="policies"
          tab={tab}
          caseType={caseType}
          caseDetail={activeItem}
          mode={action || ""}
          isEmptyCurrentView={isEmptyCurrentView}
        />
      )}
      <div className="case_container_flex">
        {action && canRenderForm &&
          (activeItemloading ? (
            <FormLoader />
          ) : (
            <CaseForm
              mode={action || ""}
              section="policies"
              caseType={caseType}
              caseDetail={activeItem}
              caseItemList={caseTypeListItems}
              tab={tab}
              notify={showToast}
              currentRecord={currentRecord}
              setCurrentRecord={setCurrentRecord}
            />
          ))}
        <div>
          {loading ? (
            <TableListLoader rows={8} columns={6} />
          ) : (
            action &&
            (caseTypeListItems.length > 0 || nonCurrentExists) && (
              <CaseList
                module="policies"
                data={caseTypeListItems}
                activeItem={activeItem}
                section="policies"
                tab={tab}
                currentRecord={currentRecord}
                setCurrentRecord={setCurrentRecord}
                showNonCurrent={showNonCurrent}
                onToggleNonCurrent={() => setShowNonCurrent((prev) => !prev)}
                emptyMessage={
                  isEmptyCurrentView
                    ? 'No active records for current cases. Click "Non-current Cases" to view non-current records.'
                    : undefined
                }
              />
            )
          )}
        </div>
      </div>
    </>
  );
};

export default PolicyDetailPage;
